'use client';

import type { FormEvent, RefObject } from 'react';
import { ArrowRightIcon, CheckCircleIcon, LoadingSpinner } from '@/components/icons';
import { SCAN_TRUST_LINE } from '@/lib/conversion-copy';
import { fuzzyResetTime } from './url-helpers';
import styles from './HomepageScanner.module.css';

type ButtonState = 'idle' | 'submitting' | 'success';

export interface HomepageScannerProps {
  inputId: string;
  inputRef: RefObject<HTMLInputElement | null>;
  inputWrapperRef: RefObject<HTMLDivElement | null>;
  inputValue: string;
  isSubmitting: boolean;
  buttonState: ButtonState;
  error: string | null;
  rateLimitInfo: { resetIn: string; resetInSeconds: number } | null;
  honeypot: string;
  placeholder: string;
  ctaLabel: string;
  label: string;
  showTrustSignal: boolean;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  setHoneypot: React.Dispatch<React.SetStateAction<string>>;
}

export function HomepageScanner({
  inputId,
  inputRef,
  inputWrapperRef,
  inputValue,
  isSubmitting,
  buttonState,
  error,
  rateLimitInfo,
  honeypot,
  placeholder,
  ctaLabel,
  label,
  showTrustSignal,
  handleSubmit,
  handleInputChange,
  handleFocus,
  setHoneypot,
}: HomepageScannerProps): React.ReactElement {
  // A message (error or rate limit) takes the trust line's slot rather than
  // stacking above it, so the controls and everything after the form hold
  // still when validation fires.
  const hasMessage = Boolean(rateLimitInfo || error);
  const showTrust = showTrustSignal && !hasMessage;
  const describedBy = [
    error ? `${inputId}-error` : null,
    showTrust ? `${inputId}-trust` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate aria-busy={isSubmitting}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <div className={styles.row}>
        <div
          ref={inputWrapperRef}
          className={styles.inputWrap}
        >
          <input
            ref={inputRef}
            id={inputId}
            className={`${styles.input} ${error ? styles.inputError : ''}`}
            type="text"
            inputMode="url"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            disabled={isSubmitting}
            placeholder={placeholder}
            autoComplete="url"
            autoCapitalize="off"
            spellCheck="false"
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy || undefined}
          />
        </div>
        <button
          type="submit"
          className={styles.button}
          disabled={isSubmitting || buttonState === 'success'}
        >
          {buttonState === 'success' ? (
            <>
              <CheckCircleIcon className="h-4 w-4" />
              <span>Opening your report...</span>
            </>
          ) : buttonState === 'submitting' ? (
            <>
              <LoadingSpinner className="h-4 w-4 animate-spin" />
              <span>Starting scan...</span>
            </>
          ) : (
            <>
              <span>{ctaLabel}</span>
              <ArrowRightIcon className="h-[14px] w-[14px]" variant="hairline" />
            </>
          )}
        </button>
      </div>

      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
        className={styles.honeypot}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {rateLimitInfo ? (
        <div className={`${styles.message} ${styles.rateLimit}`} role="status" aria-live="polite">
          You&apos;ve reached the free scan limit for now. Try again in{' '}
          {fuzzyResetTime(rateLimitInfo.resetInSeconds)}.
        </div>
      ) : error ? (
        <p id={`${inputId}-error`} className={styles.message} role="alert">
          {error}
        </p>
      ) : null}

      {showTrust ? (
        <p id={`${inputId}-trust`} className={styles.trust}>
          {SCAN_TRUST_LINE}
        </p>
      ) : null}
    </form>
  );
}
